// src/modals/AnnonceFormModal.js
// Modal création / édition d'annonce communautaire

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Megaphone, ImageIcon, Loader, MapPin } from 'lucide-react';
import { useModal } from '../hooks';
import annonceService from '../services/annonceService';
import { trackEvent } from '../services/analyticsService';
import { ANNONCE_CATEGORIES, ANNONCE_MAX_PHOTOS, ANNONCE_MAX_DESCRIPTION } from '../constants';
import { Btn } from '../components/ui';
import { searchCities } from '../utils/geocoding';
import PajNtaubBand from '../components/ui/PajNtaubBand';

const PRICE_TYPES = [
  { value: 'fixed', labelKey: 'annonceForm.priceFixed' },
  { value: 'gratuit', labelKey: 'annonceForm.priceFree' },
  { value: 'sur_devis', labelKey: 'annonceForm.priceQuote' },
];

const AnnonceFormModal = ({ isOpen, onClose, user, toast, annonce, onSuccess }) => {
  const { t } = useTranslation();
  const isEdit = !!annonce;
  const { onBackdrop } = useModal(isOpen, onClose);
  const fileRef = useRef(null);
  const cityDebounceRef = useRef(null);
  const citySelectedRef = useRef(false);

  const [form, setForm] = useState({
    title: '',
    category: '',
    description: '',
    priceType: 'gratuit',
    price: '',
    city: '',
    cityPostalCode: '',
    cityLat: null,
    cityLng: null,
  });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  // Pré-remplir en mode édition / reset en mode création
  useEffect(() => {
    if (!isOpen) return;
    if (isEdit && annonce) {
      let priceType = 'gratuit';
      let price = '';
      if (annonce.priceLabel === 'sur_devis') {
        priceType = 'sur_devis';
      } else if (
        annonce.priceLabel === 'gratuit' ||
        annonce.price === null ||
        annonce.price === 0
      ) {
        priceType = 'gratuit';
      } else {
        priceType = 'fixed';
        price = String(annonce.price);
      }
      citySelectedRef.current = true;
      setForm({
        title: annonce.title || '',
        category: annonce.category || '',
        description: annonce.description || '',
        priceType,
        price,
        city: annonce.city || '',
        cityPostalCode: annonce.cityPostalCode || '',
        cityLat: annonce.cityLat || null,
        cityLng: annonce.cityLng || null,
      });
      setPhotos((annonce.photos || []).map((url) => ({ url, status: 'done' })));
    } else {
      setForm({
        title: '',
        category: '',
        description: '',
        priceType: 'gratuit',
        price: '',
        city: '',
        cityPostalCode: '',
        cityLat: null,
        cityLng: null,
      });
      setPhotos([]);
    }
    setErrors({});
    setUploading({});
    setCitySuggestions([]);
    setShowCityDropdown(false);
  }, [isOpen, isEdit, annonce]);

  const set = (key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  // ── Autocomplete ville (Nominatim OpenStreetMap) ──

  useEffect(() => {
    if (citySelectedRef.current) {
      citySelectedRef.current = false;
      return;
    }
    if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    if (form.city.length < 2) {
      setCitySuggestions([]);
      setShowCityDropdown(false);
      return;
    }
    cityDebounceRef.current = setTimeout(async () => {
      const results = await searchCities(form.city);
      setCitySuggestions(results);
      setShowCityDropdown(results.length > 0);
    }, 300);
    return () => {
      if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    };
  }, [form.city]);

  const handleCityChange = (e) => {
    const val = e.target.value;
    setForm((prev) => ({
      ...prev,
      city: val,
      cityPostalCode: '',
      cityLat: null,
      cityLng: null,
    }));
    setErrors((prev) => ({ ...prev, city: undefined }));
  };

  const selectCity = (s) => {
    citySelectedRef.current = true;
    setShowCityDropdown(false);
    setCitySuggestions([]);
    setForm((prev) => ({
      ...prev,
      city: s.city,
      cityPostalCode: s.postcode,
      cityLat: s.lat,
      cityLng: s.lng,
    }));
    setErrors((prev) => ({ ...prev, city: undefined }));
  };

  // ── Upload photos ──

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const totalAfter = photos.length + files.length;
    if (totalAfter > ANNONCE_MAX_PHOTOS) {
      setErrors((prev) => ({
        ...prev,
        photos: t('annonceForm.photosMaxError', {
          max: ANNONCE_MAX_PHOTOS,
          count: photos.length,
          plural: photos.length !== 1 ? 's' : '',
        }),
      }));
      return;
    }
    setErrors((prev) => ({ ...prev, photos: undefined }));

    for (const file of files) {
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const localUrl = URL.createObjectURL(file);

      setPhotos((prev) => [...prev, { url: localUrl, status: 'uploading', tempId }]);
      setUploading((prev) => ({ ...prev, [tempId]: true }));

      try {
        const result = await annonceService.uploadAnnoncePhoto(user.uid, file);
        if (result.success) {
          setPhotos((prev) =>
            prev.map((p) => (p.tempId === tempId ? { url: result.url, status: 'done' } : p))
          );
        } else {
          setPhotos((prev) => prev.filter((p) => p.tempId !== tempId));
          toast(t('annonceForm.toastUploadError'), 'error');
        }
      } catch {
        setPhotos((prev) => prev.filter((p) => p.tempId !== tempId));
        toast(t('annonceForm.toastUploadError'), 'error');
      } finally {
        setUploading((prev) => {
          const next = { ...prev };
          delete next[tempId];
          return next;
        });
        URL.revokeObjectURL(localUrl);
      }
    }

    // Reset file input
    if (fileRef.current) fileRef.current.value = '';
  };

  const removePhoto = (index) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
    setErrors((prev) => ({ ...prev, photos: undefined }));
  };

  // ── Validation ──

  const validate = () => {
    const errs = {};
    if (!form.title.trim()) errs.title = t('annonceForm.validation.title');
    if (!form.category) errs.category = t('annonceForm.validation.category');
    if (!form.description.trim()) errs.description = t('annonceForm.validation.description');
    if (form.description.length > ANNONCE_MAX_DESCRIPTION)
      errs.description = t('annonceForm.validation.descMax', { max: ANNONCE_MAX_DESCRIPTION });
    if (!form.city.trim()) errs.city = t('annonceForm.validation.city');
    if (
      form.priceType === 'fixed' &&
      (form.price === '' || isNaN(Number(form.price)) || Number(form.price) < 0)
    )
      errs.price = t('annonceForm.validation.price');
    if (photos.length > ANNONCE_MAX_PHOTOS)
      errs.photos = t('annonceForm.validation.photosMax', { max: ANNONCE_MAX_PHOTOS });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Soumission ──

  const handleSubmit = async () => {
    if (!validate()) return;
    if (Object.keys(uploading).length > 0) {
      toast(t('annonceForm.toastWaitUpload'), 'warning');
      return;
    }

    setLoading(true);

    const photoUrls = photos.filter((p) => p.status === 'done').map((p) => p.url);

    let price = null;
    let priceLabel = null;
    if (form.priceType === 'gratuit') {
      priceLabel = 'gratuit';
    } else if (form.priceType === 'sur_devis') {
      priceLabel = 'sur_devis';
    } else {
      price = Number(form.price);
    }

    const data = {
      title: form.title.trim(),
      category: form.category,
      description: form.description.trim(),
      price,
      priceLabel,
      city: form.city.trim(),
      cityPostalCode: form.cityPostalCode || null,
      cityLat: form.cityLat || null,
      cityLng: form.cityLng || null,
      photos: photoUrls,
    };

    let result;
    if (isEdit) {
      result = await annonceService.updateAnnonce(annonce.id, user.uid, data);
    } else {
      result = await annonceService.createAnnonce(user.uid, data);
    }

    setLoading(false);

    if (result.success) {
      if (!isEdit) {
        trackEvent('annonce_created', { category: form.category });
      }
      toast(isEdit ? t('annonceForm.toastEdited') : t('annonceForm.toastCreated'), 'success');
      onSuccess?.();
      onClose();
    } else {
      const errorMsg =
        result.error === 'max_active_reached'
          ? t('annonceForm.toastMaxActive')
          : result.error || t('annonceForm.toastError');
      toast(errorMsg, 'error');
    }
  };

  if (!isOpen) return null;

  const isUploading = Object.keys(uploading).length > 0;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 animate-fade-in"
      onClick={onBackdrop}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-lg animate-slide-up max-h-[85dvh] overflow-hidden"
        style={{ overscrollBehavior: 'contain' }}
        data-testid="annonce-form-modal"
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? t('annonceForm.ariaEdit') : t('annonceForm.ariaCreate')}
      >
        <PajNtaubBand height={4} />
        <div
          className={`p-4 sm:p-6 ${showCityDropdown ? 'overflow-visible' : 'overflow-y-auto'}`}
          style={{ maxHeight: 'calc(85dvh - 4px)' }}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base font-semibold dark:text-gray-100">
              {isEdit ? t('annonceForm.titleEdit') : t('annonceForm.titleCreate')}
            </h3>
            <button
              onClick={onClose}
              aria-label={t('annonceForm.ariaClose')}
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X size={20} />
            </button>
          </div>

          <div className="text-center mb-5">
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-full mx-auto flex items-center justify-center text-white mb-2">
              <Megaphone size={24} />
            </div>
          </div>

          <div className="space-y-4">
            {/* Titre */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('annonceForm.titleLabel')} <span className="text-indigo-500">*</span>
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder={t('annonceForm.titlePlaceholder')}
                maxLength={100}
                className={`w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 ${
                  errors.title
                    ? 'border-red-300'
                    : 'border-gray-100 dark:border-gray-700 focus:border-indigo-300 dark:focus:border-indigo-500'
                } rounded-xl dark:text-gray-100 focus:outline-none transition`}
                data-testid="annonce-form-title"
              />
              {errors.title && (
                <p className="text-red-500 text-sm" data-testid="annonce-form-error-title">
                  {errors.title}
                </p>
              )}
            </div>

            {/* Catégorie */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('annonceForm.categoryLabel')} <span className="text-indigo-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {ANNONCE_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => set('category', cat.id)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                      form.category === cat.id
                        ? 'bg-indigo-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                    data-testid={`annonce-form-category-${cat.id}`}
                  >
                    {t(cat.labelKey)}
                  </button>
                ))}
              </div>
              {errors.category && (
                <p className="text-red-500 text-sm" data-testid="annonce-form-error-category">
                  {errors.category}
                </p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('annonceForm.descLabel')} <span className="text-indigo-500">*</span>
              </label>
              <textarea
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder={t('annonceForm.descPlaceholder')}
                maxLength={ANNONCE_MAX_DESCRIPTION}
                rows={4}
                className={`w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 ${
                  errors.description
                    ? 'border-red-300'
                    : 'border-gray-100 dark:border-gray-700 focus:border-indigo-300 dark:focus:border-indigo-500'
                } rounded-xl dark:text-gray-100 focus:outline-none transition resize-none`}
                data-testid="annonce-form-description"
              />
              <div className="flex justify-between">
                {errors.description && (
                  <p className="text-red-500 text-sm" data-testid="annonce-form-error-description">
                    {errors.description}
                  </p>
                )}
                <p className="text-gray-400 text-xs ml-auto">
                  {form.description.length}/{ANNONCE_MAX_DESCRIPTION}
                </p>
              </div>
            </div>

            {/* Prix */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('annonceForm.priceLabel')}
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {PRICE_TYPES.map((pt) => (
                  <button
                    key={pt.value}
                    type="button"
                    onClick={() => set('priceType', pt.value)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                      form.priceType === pt.value
                        ? 'bg-indigo-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {t(pt.labelKey)}
                  </button>
                ))}
              </div>
              {form.priceType === 'fixed' && (
                <div>
                  <input
                    type="number"
                    value={form.price}
                    onChange={(e) => set('price', e.target.value)}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    className={`w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 ${
                      errors.price
                        ? 'border-red-300'
                        : 'border-gray-100 dark:border-gray-700 focus:border-indigo-300 dark:focus:border-indigo-500'
                    } rounded-xl dark:text-gray-100 focus:outline-none transition`}
                    data-testid="annonce-form-price"
                  />
                  {errors.price && (
                    <p className="text-red-500 text-sm mt-1" data-testid="annonce-form-error-price">
                      {errors.price}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Ville (autocomplete) */}
            <div className="relative space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('annonceForm.cityLabel')} <span className="text-indigo-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                  <MapPin size={18} />
                </div>
                <input
                  type="text"
                  value={form.city}
                  onChange={handleCityChange}
                  placeholder={t('annonceForm.cityPlaceholder')}
                  className={`w-full pl-11 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 ${
                    errors.city
                      ? 'border-red-300'
                      : 'border-gray-100 dark:border-gray-700 focus:border-indigo-300 dark:focus:border-indigo-500'
                  } rounded-xl dark:text-gray-100 focus:outline-none transition`}
                  data-testid="annonce-form-city"
                />
              </div>

              {showCityDropdown && citySuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
                  {citySuggestions.map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => selectCity(s)}
                      data-testid="city-suggestion"
                      className="w-full px-4 py-3 text-left hover:bg-indigo-50 dark:hover:bg-gray-800 transition flex items-center gap-2 text-sm dark:text-gray-200"
                    >
                      <MapPin size={14} className="text-gray-400 flex-shrink-0" />
                      <span>{s.label}</span>
                    </button>
                  ))}
                </div>
              )}

              {errors.city && (
                <p className="text-red-500 text-sm" data-testid="annonce-form-error-city">
                  {errors.city}
                </p>
              )}

              {form.cityPostalCode && (
                <div className="mt-1 p-2.5 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl flex items-center gap-2">
                  <MapPin
                    size={14}
                    className="text-emerald-600 dark:text-emerald-400 flex-shrink-0"
                  />
                  <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                    {form.city} ({form.cityPostalCode})
                  </span>
                </div>
              )}
            </div>

            {/* Photos */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('annonceForm.photosLabel')}{' '}
                <span className="font-normal text-gray-400">
                  ({photos.length}/{ANNONCE_MAX_PHOTOS})
                </span>
              </label>

              {/* Thumbnails */}
              {photos.length > 0 && (
                <div className="flex gap-2 flex-wrap mb-2">
                  {photos.map((photo, i) => (
                    <div
                      key={photo.tempId || photo.url}
                      className="relative w-20 h-20 rounded-lg overflow-hidden"
                      data-testid={`annonce-form-photo-${i}`}
                    >
                      <img
                        src={photo.url}
                        alt={t('annonceForm.photoAlt', { index: i + 1 })}
                        className="w-full h-full object-cover"
                      />
                      {photo.status === 'uploading' && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <Loader size={18} className="text-white animate-spin" />
                        </div>
                      )}
                      {photo.status === 'done' && (
                        <button
                          type="button"
                          onClick={() => removePhoto(i)}
                          className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-0.5 hover:bg-black/70 transition"
                          aria-label={t('annonceForm.removePhotoAria', { index: i + 1 })}
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Bouton ajouter */}
              {photos.length < ANNONCE_MAX_PHOTOS && (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full h-20 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl flex flex-col items-center justify-center gap-1.5 text-gray-400 hover:border-indigo-300 dark:hover:border-indigo-500 hover:text-indigo-400 transition"
                  data-testid="annonce-form-photo-upload"
                >
                  <ImageIcon size={20} />
                  <span className="text-xs">{t('annonceForm.addPhotosHint')}</span>
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFiles}
                className="hidden"
              />
              {errors.photos && (
                <p className="text-red-500 text-sm" data-testid="annonce-form-error-photos">
                  {errors.photos}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <Btn
              v="ghost"
              onClick={onClose}
              className="flex-1 text-sm py-2.5"
              data-testid="annonce-form-cancel"
            >
              {t('common.cancel')}
            </Btn>
            <Btn
              onClick={handleSubmit}
              loading={loading}
              disabled={isUploading}
              className="flex-1 text-sm py-2.5"
              data-testid="annonce-form-submit"
            >
              {isEdit ? t('common.save') : t('common.publish')}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnonceFormModal;
